<template>
	<div>
		<CRow class="mb-4 text-right">
			<CCol>
				<router-link
					:to="{ name: createLink }"
					class="btn btn-secondary"
				>
					<CIcon class="mr-2" name="cil-plus" /> {{ createButtonText || $t('global.general.create', { type: 'Banner' }) }}
				</router-link>
			</CCol>
		</CRow>
		<CRow>
			<CCol class="d-flex align-items-start">
				<form class="flex-fill" @submit.prevent="handleSearch">
					<CInput
						v-model.trim="queryParams.q"
						:placeholder="placeholderText || this.$t('global.general.searchPlaceholder', { type: 'Banner' })"
					>
						<template #prepend-content>
							<CIcon name="cil-magnifying-glass" />
						</template>
					</CInput>
				</form>
				<BaseFilterButton
					class="ml-3"
					:is-open="isShowFilter"
					:has-filter="hasFilter"
					@onClick="toggleFilter"
				/>
			</CCol>
		</CRow>

		<CRow class="mb-2">
			<CCol>
				<transition name="fade">
					<div v-if="isShowFilter" class="filter-box rounded-sm p-3">
						<CRow>
							<CCol v-if="isShowFilterDateRange" md="3">
								<BaseInputDate
									:value="dateRange"
									label="Date"
									placeholder="Start date - End date"
									mode="range"
									@input="handleFilterDateChange"
								>
									<template #append-content>
										<CIcon class="cil-calendar" name="cil-calendar" />
									</template>
								</BaseInputDate>
							</CCol>
							<CCol v-if="showLinkToFilter" md="3">
								<BaseDropDown
									:value="
										getSelectedValue(
											queryParams.widget_id,
											widgetOptions,
										)
									"
									:searchable="false"
									:options="widgetOptions"
									:allow-empty="false"
									class="select-custom"
									label="name"
									track-by="value"
									label-drop-down="Links to"
									@input="handleWidget"
								/>
							</CCol>
							<CCol v-if="showPageWidgetFilter" md="3">
								<BaseDropDown
									:value="
										getSelectedValue(
											queryParams.page_widget_id,
											pageWidgetOptions,
										)
									"
									:searchable="pageWidgetSearchable"
									:options="pageWidgetOptions"
									:allow-empty="false"
									class="select-custom"
									label="name"
									track-by="value"
									label-drop-down="Page Links to"
									@input="handlePageWidget"
								/>
							</CCol>
							<CCol md="3">
								<BaseDropDown
									:value="
										getSelectedValue(
											queryParams.is_visible,
											STATUS_OPTIONS,
										)
									"
									:searchable="false"
									:options="STATUS_OPTIONS"
									:allow-empty="false"
									class="select-custom"
									label="name"
									track-by="value"
									label-drop-down="Status"
									@input="handleInputStatus"
								/>
							</CCol>
						</CRow>
					</div>
				</transition>
			</CCol>
		</CRow>

		<CRow class="mb-4">
			<CCol class="d-flex justify-content-between">
				<div class="counter">
					{{ $t('global.general.found', { count: found, type: typeFoundText }) }}
				</div>
				<a
					href="#"
					class="btn-reset link-button"
					type="link"
					@click.prevent="resetFilter"
				>
					Reset search and filters
				</a>
			</CCol>
		</CRow>
	</div>
</template>

<script>
import {
	cleanObject,
	cloneDeep,
	hasSearchFilter,
	convertDateRangeToUTC,
} from '../assets/js/helpers';
import { STATUS_OPTIONS, BANNER_TYPE } from '../enums/banners';

export default {
	name: 'BannerCreateAndFilter',

	props: {
		createLink: {
			type: String,
			default: '',
		},
		found: {
			type: [Number, String],
			default: 0,
		},
		widgetOptions: {
			type: Array,
			default: () => [],
		},
		pageWidgetOptions: {
			type: Array,
			default: () => [],
		},
		bannerType: {
			type: String,
			default: null,
		},
		hasLinkToFilter: {
			type: Boolean,
			default: true,
		},
		pageWidgetSearchable: {
			type: Boolean,
			default: true,
		},
		isShowFilterDateRange: {
			type: Boolean,
			default: true,
		},

		// Label
		typeFoundText: {
			type: String,
			default: 'Banner',
		},
		createButtonText: {
			type: String,
			default: '',
		},
		placeholderText: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			BANNER_TYPE,
			STATUS_OPTIONS,
			queryParams: {
				q: this.$route.query.q || null,
				page: Number(this.$route.query.page) || null,
				per_page: Number(this.$route.query.per_page) || null,
				is_visible: (Number(this.$route.query.is_visible) === 0 || Number(this.$route.query.is_visible) === 1)
					? Number(this.$route.query.is_visible)
					: null,
				start_at: this.$route.query.start_at || null,
				end_at: this.$route.query.end_at || null,
				widget_id: Number(this.$route.query.widget_id) || null,
				page_widget_id: Number(this.$route.query.page_widget_id) || null,
			},
			isShowFilter: false,
		};
	},
	computed: {
		dateRange() {
			if (!this.queryParams.start_at || !this.queryParams.end_at) {
				return null;
			}

			return {
				start: new Date(this.queryParams.start_at),
				end: new Date(this.queryParams.end_at),
			};
		},
		showPageWidgetFilter() {
			return this.pageWidgetOptions && this.pageWidgetOptions.length > 1; // "All" option is always there
		},
		hasFilter() {
			return hasSearchFilter(this.queryParams);
		},
		showLinkToFilter() {
			return this.hasLinkToFilter
				&& this.bannerType !== BANNER_TYPE.hero
				&& this.bannerType !== BANNER_TYPE.custom_hero
				&& this.bannerType !== BANNER_TYPE.lob;
		},
	},
	created() {
		this.isShowFilter = this.hasFilter;
	},
	methods: {
		updateUrlParams() {
			const query = cleanObject(cloneDeep(this.queryParams));
			this.$router.push({ query }).catch(() => {});
		},
		handlePageChange(page) {
			this.queryParams.page = page;
			this.updateUrlParams();
		},
		handleSearch() {
			this.queryParams.page = null;
			this.updateUrlParams();
		},
		handleInputStatus(event) {
			this.queryParams.page = null;
			this.queryParams.is_visible = event.value;
			this.updateUrlParams();
		},
		handleFilterDateChange(event) {
			const { start, end } = event ? convertDateRangeToUTC(event) : {};

			this.queryParams.page = null;
			this.queryParams.start_at = start;
			this.queryParams.end_at = end;
			this.updateUrlParams();
		},
		handleWidget(event) {
			this.queryParams.page = null;
			this.queryParams.widget_id = event.value;
			this.queryParams.page_widget_id = null;
			this.updateUrlParams();
		},
		handlePageWidget(event) {
			this.queryParams.page = null;
			this.queryParams.page_widget_id = event.value;
			this.queryParams.widget_id = null;
			this.updateUrlParams();
		},
		getSelectedValue(selectedValue = null, options = []) {
			const optionValues = Object.values(options);
			const found = ({ value }) => value === selectedValue;

			return (
				optionValues.find(found) || {
					name: null,
					value: null,
				}
			);
		},
		toggleFilter() {
			this.isShowFilter = !this.isShowFilter;
		},
		resetFilter() {
			this.queryParams = {
				q: null,
				page: null,
				per_page: null,
				is_visible: null,
				page_widget_id: null,
				widget_id: null,
			};

			this.updateUrlParams();
		},
	},
};
</script>

<style lang="scss" scoped>
	.filter-box {
		background-color: $color-gray-100;
	}

	.counter {
		font-size: rem(14);
		color: $color-black-70;
	}

	.btn-reset {
		font-size: rem(14);
	}
</style>
